import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Container } from '@common/components/container';
import DataTable from '@common/components/datatable';
import FiltersToolbar from '@common/components/filters-toolbar/filters-toolbar';
import Overview from '@common/components/overview';
import Placeholder from '@common/components/placeholder';
import ProfileImage from '@common/components/profile-image';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useDidComponentMount } from '@common/hooks';
import * as organisationSelectors from '../../../organisation/selectors/organisation';
import { EPredicateFilters } from '@common/components/predicates-filter';
import { EPredicateFields, EPredicateOperators } from '@common/definitions';
import Icon from '@common/components/icon';
import Profile from '@common/components/profile';
import { useRequest } from '@common/services/api';
import useColumns from './useColumns';
import { omit } from 'lodash';
import Spinner from '@common/components/spinner';
import PerformanceTooltip from '@modules/performance/components/performance_tooltip/performance_tooltip';
import ModalWindow from '@common/components/modal/modal-window';
import ModalContent from '@common/components/modal/modal-content';
import { SortConfig } from '@common/components/datatable/types';

// Static employees for testing
// const employees: Array<{
//   id: string;
//   full_name: string;
//   profile_img?: string;
// }> = [
//   {
//     id: '1',
//     full_name: 'Ava Taylor',
//     profile_img: 'https://i.pravatar.cc/80?img=8',
//   },
//   {
//     id: '2',
//     full_name: 'Nathan Hayes',
//     profile_img: 'https://i.pravatar.cc/80?img=15',
//   },
//   {
//     id: '3',
//     full_name: 'Michael Reeves',
//     profile_img: 'https://i.pravatar.cc/80?img=13',
//   },
//   {
//     id: '4',
//     full_name: 'Emily Parker',
//     profile_img: 'https://i.pravatar.cc/80?img=5',
//   },
//   {
//     id: '5',
//     full_name: 'Lauren Scott',
//     profile_img: 'https://i.pravatar.cc/80?img=20',
//   },
//   {
//     id: '6',
//     full_name: 'Jessica Lane',
//     profile_img: 'https://i.pravatar.cc/80?img=29',
//   },
//   {
//     id: '7',
//     full_name: 'Isabella Martinez',
//     profile_img: 'https://i.pravatar.cc/80?img=32',
//   },
//   {
//     id: '8',
//     full_name: 'Lucas Green',
//     profile_img: 'https://i.pravatar.cc/80?img=22',
//   },
//   {
//     id: '9',
//     full_name: 'Ava Taylor',
//     profile_img: 'https://i.pravatar.cc/80?img=8',
//   },
//   {
//     id: '10',
//     full_name: 'Nathan Hayes',
//     profile_img: 'https://i.pravatar.cc/80?img=15',
//   },
//   {
//     id: '11',
//     full_name: 'Emily Parker',
//     profile_img: 'https://i.pravatar.cc/80?img=5',
//   },
//   {
//     id: '12',
//     full_name: 'Jessica Lane',
//     profile_img: 'https://i.pravatar.cc/80?img=29',
//   },
//   {
//     id: '13',
//     full_name: 'Isabella Martinez',
//     profile_img: 'https://i.pravatar.cc/80?img=32',
//   },
//   {
//     id: '14',
//     full_name: 'Lucas Green',
//     profile_img: 'https://i.pravatar.cc/80?img=22',
//   },
// ];

const calculateRetentionRate = (
  retentionRisk: Array<{ score: number; total_users: number }>
): number => {
  if (
    !retentionRisk ||
    !Array.isArray(retentionRisk) ||
    retentionRisk.length === 0
  ) {
    return 0;
  }

  const lowCount = retentionRisk.find((r) => r.score === 1)?.total_users || 0;
  const mediumCount =
    retentionRisk.find((r) => r.score === 2)?.total_users || 0;
  const highCount = retentionRisk.find((r) => r.score === 3)?.total_users || 0;

  const total = lowCount + mediumCount + highCount;
  if (total === 0) {
    return 0;
  }
  const retentionRate = ((mediumCount + highCount) / total) * 100;
  return Math.round(retentionRate);
};

const gridKeyOptions = [
  { label: 'Risk', value: 'risk' },
  { label: 'Inconsistent Player', value: 'inconsistent_player' },
  { label: 'Potential Gem', value: 'potential_gem' },
  { label: 'Average Performer', value: 'average_performer' },
  { label: 'Core Player', value: 'core_player' },
  { label: 'High Potential', value: 'high_potential' },
  { label: 'Solid Performer', value: 'solid_performer' },
  { label: 'High Performer', value: 'high_performer' },
  { label: 'Top Talent', value: 'top_talent' },
] as const;

const getGridKeyFromCell = (
  potential: number,
  performance: number
): string | null => {
  const gridKeyMap: Record<string, string> = {
    '3-1': 'potential_gem',
    '3-2': 'high_potential',
    '3-3': 'top_talent',
    '2-1': 'average_performer',
    '2-2': 'core_player',
    '2-3': 'high_performer',
    '1-1': 'risk',
    '1-2': 'inconsistent_player',
    '1-3': 'solid_performer',
  };
  return gridKeyMap[`${potential}-${performance}`] || null;
};

const TalentReviewsContainer = () => {
  const { t } = useTranslation();
  const networks = useAppSelector(organisationSelectors.networks);
  const functions = useAppSelector(organisationSelectors.functions);
  const organisation = useAppSelector(organisationSelectors.selected);
  const didMount = useDidComponentMount();
  const [activeTab, setActiveTab] = useState('table');
  const [allTalentReviews, setAllTalentReviews] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig | undefined>();

  const getBasePredicates = () => [
    {
      filter: EPredicateFilters[EPredicateFields.NETWORK],
      value: {
        attribute: EPredicateFields.NETWORK,
        comparison: EPredicateOperators.IN,
        value: [],
      },
      options: networks || [],
    },
    {
      filter: EPredicateFilters[EPredicateFields.FUNCTION],
      value: {
        attribute: EPredicateFields.FUNCTION,
        comparison: EPredicateOperators.IN,
        value: [],
      },
      options: functions || [],
    },
  ];

  const [predicates, setPredicates] = useState(() => {
    const base = getBasePredicates();
    if (activeTab === 'grid') {
      return [
        ...base,
        {
          filter: EPredicateFilters[EPredicateFields.GRID_KEY],
          value: {
            attribute: EPredicateFields.GRID_KEY,
            comparison: EPredicateOperators.IN,
            value: [],
          },
          options: gridKeyOptions,
        },
      ];
    }
    return base;
  });
  // const [showDrawer, setShowDrawer] = useState(false);
  // const [selectedUser, setSelectedUser] =
  //   useState<{ id: number; name: string } | null>(null);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [modalCellData, setModalCellData] =
    useState<{
      title: string;
      users: Array<{ id: string; full_name: string; profile_img?: string }>;
    } | null>(null);
  const prevActiveTabRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      prevActiveTabRef.current !== null &&
      prevActiveTabRef.current !== activeTab
    ) {
      const currentNetworkIds =
        predicates.find(
          (p) => p.filter === EPredicateFilters[EPredicateFields.NETWORK]
        )?.value.value || [];
      const currentFunctionIds =
        predicates.find(
          (p) => p.filter === EPredicateFilters[EPredicateFields.FUNCTION]
        )?.value.value || [];

      const base = [
        {
          filter: EPredicateFilters[EPredicateFields.NETWORK],
          value: {
            attribute: EPredicateFields.NETWORK,
            comparison: EPredicateOperators.IN,
            value: currentNetworkIds,
          },
          options: networks || [],
        },
        {
          filter: EPredicateFilters[EPredicateFields.FUNCTION],
          value: {
            attribute: EPredicateFields.FUNCTION,
            comparison: EPredicateOperators.IN,
            value: currentFunctionIds,
          },
          options: functions || [],
        },
      ];

      if (activeTab === 'grid') {
        const currentGridKeys =
          predicates.find(
            (p) => p.filter === EPredicateFilters[EPredicateFields.GRID_KEY]
          )?.value.value || [];

        setPredicates([
          ...base,
          {
            filter: EPredicateFilters[EPredicateFields.GRID_KEY],
            value: {
              attribute: EPredicateFields.GRID_KEY,
              comparison: EPredicateOperators.IN,
              value: currentGridKeys,
            },
            options: gridKeyOptions,
          },
        ]);
      } else {
        setPredicates(base);
      }
    }
    prevActiveTabRef.current = activeTab;
  }, [activeTab, networks, functions, predicates]);

  const renderAvatarGroup = (
    users: Array<{
      id: string;
      full_name: string;
      profile_img?: string;
    }>,
    cellTitle?: string
  ) => {
    const maxVisible = 5;
    const visible = users.slice(0, maxVisible);
    const extra = users.length - visible.length;
    return (
      <div
        className="tw-flex tw-items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tw-flex -tw-space-x-2">
          {visible.map((user) => (
            <a
              href={`/admin/users/${user.id}/about`}
              target="_blank"
              key={user.id}
              rel="noreferrer"
            >
              <ProfileImage
                enablePopover
                key={user.id}
                className="tw-rounded-full tw-ring-2 tw-ring-white"
                user={user}
                size={28}
                // onClick={() => {
                //   setSelectedUser({
                //     id: Number.parseInt(user.id, 10),
                //     name: user.full_name,
                //   });
                //   setShowDrawer(true);
                // }}
              />
            </a>
          ))}
          {extra > 0 && (
            <div
              className="tw-rounded-full tw-border tw-border-white tw-bg-gray-50 tw-text-gray-700
              tw-text-[11px] tw-font-medium tw-grid tw-place-items-center
              tw-z-10 tw-shrink-0 tw-cursor-pointer hover:tw-bg-gray-100"
              style={{ width: 28, height: 28 }}
              onClick={(e) => {
                e.stopPropagation();
                setModalCellData({
                  title: cellTitle || '',
                  users,
                });
                setShowUsersModal(true);
              }}
            >
              +{extra}
            </div>
          )}
        </div>
      </div>
    );
  };

  const PercentagePill: React.FC<{
    value: string;
    tone: 'green' | 'orange' | 'red';
    retentionRisk?: Array<{ score: number; total_users: number }>;
  }> = ({ value, tone, retentionRisk = [] }) => {
    const percent = Number.parseInt(value, 10) || 0;
    const clamped = Math.max(0, Math.min(100, percent));

    const angle = clamped * 3.6;
    const color =
      tone === 'green' ? '#22c55e' : tone === 'orange' ? '#f59e0b' : '#ef4444';

    const retentionRiskMap = {
      1: { label: 'Low', color: 'tw-bg-green-500' },
      2: { label: 'Moderate', color: 'tw-bg-orange-500' },
      3: { label: 'High', color: 'tw-bg-red-500' },
    };

    const lowCount = retentionRisk.find((r) => r.score === 1)?.total_users || 0;
    const moderateCount =
      retentionRisk.find((r) => r.score === 2)?.total_users || 0;
    const highCount =
      retentionRisk.find((r) => r.score === 3)?.total_users || 0;

    return (
      <PerformanceTooltip
        title={
          <div className="tw-py-3 tw-min-w-[180px]">
            <div className="tw-text-gray-400 tw-text-xs tw-font-medium tw-uppercase tw-mb-3">
              {t('performance:retention_risk')}
            </div>
            <div className="tw-flex tw-flex-col tw-gap-2">
              <div className="tw-flex tw-flex-row tw-items-center tw-justify-between tw-gap-4">
                <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
                  <div className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-green-500" />
                  <span className="tw-text-white tw-text-sm tw-font-medium">
                    {retentionRiskMap[1].label}
                  </span>
                </div>
                <span className="tw-text-white tw-text-sm tw-font-medium">
                  {lowCount}
                </span>
              </div>
              <div className="tw-flex tw-flex-row tw-items-center tw-justify-between tw-gap-4">
                <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
                  <div className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-orange-500" />
                  <span className="tw-text-white tw-text-sm tw-font-medium">
                    {retentionRiskMap[2].label}
                  </span>
                </div>
                <span className="tw-text-white tw-text-sm tw-font-medium">
                  {moderateCount}
                </span>
              </div>
              <div className="tw-flex tw-flex-row tw-items-center tw-justify-between tw-gap-4">
                <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
                  <div className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-red-500" />
                  <span className="tw-text-white tw-text-sm tw-font-medium">
                    {retentionRiskMap[3].label}
                  </span>
                </div>
                <span className="tw-text-white tw-text-sm tw-font-medium">
                  {highCount}
                </span>
              </div>
            </div>
          </div>
        }
      >
        <div
          className="tw-flex tw-items-center tw-gap-2 tw-rounded-full
        tw-border tw-border-solid tw-border-gray-200 tw-py-1 tw-pl-1 tw-pr-2"
        >
          <span
            className="tw-rounded-full tw-grid tw-place-items-center"
            style={{
              width: 18,
              height: 18,
              background: `conic-gradient(${color} ${angle}deg, #e5e7eb ${angle}deg)`,
            }}
          >
            <span
              className="tw-bg-white tw-rounded-full"
              style={{ width: 12, height: 12 }}
            />
          </span>
          <span className="tw-text-xs tw-text-gray-700">{value}</span>
        </div>
      </PerformanceTooltip>
    );
  };

  const getToneFromColor = (color: string): 'green' | 'orange' | 'red' => {
    if (
      color.includes('22C55E') ||
      color.includes('16A34A') ||
      color.includes('15803D') ||
      color.includes('11C55E')
    ) {
      return 'green';
    }
    if (
      color.includes('EA580C') ||
      color.includes('F97316') ||
      color.includes('F59E0B')
    ) {
      return 'orange';
    }
    return 'red';
  };

  // Handle grid cell click to navigate to list view with the grid key filter
  const handleGridCellClick = (potential: number, performance: number) => {
    const gridKey = getGridKeyFromCell(potential, performance);
    if (!gridKey) return;

    // Get current filter values
    const currentNetworkIds =
      predicates.find(
        (p) => p.filter === EPredicateFilters[EPredicateFields.NETWORK]
      )?.value.value || [];
    const currentFunctionIds =
      predicates.find(
        (p) => p.filter === EPredicateFilters[EPredicateFields.FUNCTION]
      )?.value.value || [];

    // Set predicates with grid key filter
    setPredicates([
      {
        filter: EPredicateFilters[EPredicateFields.NETWORK],
        value: {
          attribute: EPredicateFields.NETWORK,
          comparison: EPredicateOperators.IN,
          value: currentNetworkIds,
        },
        options: networks || [],
      },
      {
        filter: EPredicateFilters[EPredicateFields.FUNCTION],
        value: {
          attribute: EPredicateFields.FUNCTION,
          comparison: EPredicateOperators.IN,
          value: currentFunctionIds,
        },
        options: functions || [],
      },
      {
        filter: EPredicateFilters[EPredicateFields.GRID_KEY],
        value: {
          attribute: EPredicateFields.GRID_KEY,
          comparison: EPredicateOperators.IN,
          value: [gridKey] as any,
        },
        options: gridKeyOptions,
      },
    ]);

    // Switch to list view (grid tab)
    setActiveTab('grid');
  };

  const getPotentialLabelColor = (potential: number) => {
    if (potential === 3) return 'tw-bg-alert-success-softlight';
    if (potential === 2) return 'tw-bg-alert-warning-soft';
    return 'tw-bg-alert-error-default';
  };

  const getPerformanceLabelColor = (performance: number) => {
    if (performance === 1) return 'tw-bg-alert-error-default';
    if (performance === 2) return 'tw-bg-alert-warning-soft';
    return 'tw-bg-alert-success-softlight';
  };

  const NineBoxGrid = () => {
    if (gridLoading) {
      return (
        <div className="tw-w-full tw-flex tw-items-center tw-justify-center tw-py-12">
          <div className="tw-text-gray-500">
            <Spinner centered />
          </div>
        </div>
      );
    }

    if (!gridDataRaw && !gridLoading) {
      return (
        <div className="tw-w-full tw-flex tw-items-center tw-justify-center tw-py-12">
          <Placeholder title={t('performance:no_employees_placeholder')} />
        </div>
      );
    }

    // Check if all cells are empty (all null)
    const hasData = nineBoxCells.some((row) => row.some((cell) => cell !== null)
    );

    if (!hasData && !gridLoading) {
      return (
        <div className="tw-w-full tw-flex tw-items-center tw-justify-center tw-py-12">
          <Placeholder title={t('performance:no_employees_placeholder')} />
        </div>
      );
    }

    return (
      <div className="tw-w-full">
        <div className="tw-relative tw-pb-8 tw-pl-12 tw-pt-3">
          {/* Rotated "Potential" label on the left */}
          <div
            className="tw-absolute tw-left-[-20px] tw-top-0
          tw-bottom-[120px] tw-flex tw-items-center tw-justify-center tw-w-12"
          >
            <span className="tw-text-gray-500 tw-text-base tw-font-medium tw--rotate-90 tw-whitespace-nowrap">
              {t('performance:potential_label')}
            </span>
          </div>

          {/* Left vertical border */}
          <div className="tw-absolute !tw-left-6 tw-top-3 tw-bottom-[134px] tw-w-[2px] tw-bg-gray-200" />

          {/* Grid content */}
          <div className="tw-flex tw-flex-col tw-gap-4">
            {/* Rows with slim left bars and 3 cells */}
            <div className="tw-space-y-4">
              {nineBoxCells.map((row, rIdx) => {
                const potentialValue = row[0]?.potential || 3 - rIdx;
                return (
                  <div
                    key={`row-potential-${potentialValue}`}
                    className="tw-grid tw-grid-cols-4 tw-gap-4"
                    style={{ gridTemplateColumns: '40px 0.5fr 0.5fr 0.5fr' }}
                  >
                    {/* Slim label bar */}
                    <div
                      className={`tw-rounded-md tw-text-gray-textDark ${getPotentialLabelColor(
                        potentialValue
                      )}`}
                    >
                      <div className="tw-h-full tw-min-h-[140px] tw-flex tw-items-center tw-justify-center">
                        <span className="tw-text-base tw-font-medium tw--rotate-90
                        tw-text-gray-textDark tw-block tw-whitespace-nowrap"
                        >
                          {reversedPotentialLabels[rIdx] || ''}{' '}
                          <span className="tw-text-sm tw-text-gray-500">
                            ({potentialDistribution[rIdx] || 0}%)
                          </span>
                        </span>
                      </div>
                    </div>

                    {row.map((cell, idx) => {
                      if (!cell) {
                        const performanceValue = idx + 1;
                        return (
                          <div
                            key={`cell-potential-${potentialValue}-performance-${performanceValue}`}
                            className="tw-border tw-border-gray-200 tw-rounded-md tw-bg-white
                            tw-p-4 tw-flex tw-flex-col tw-justify-between tw-min-h-[140px] tw-opacity-50"
                          >
                            <div className="tw-flex tw-justify-between tw-items-start">
                              <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
                                <div className="tw-w-[10px] tw-h-4 tw-rounded-[3px] tw-bg-gray-300" />
                                <div className="tw-flex tw-flex-row tw-items-center tw-gap-1">
                                  <div className="tw-text-base tw-font-semibold tw-text-gray-400">
                                    -
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="tw-mt-3 tw-flex tw-items-center tw-justify-between">
                              <div className="tw-flex tw-items-center">
                                <div className="tw-text-sm tw-text-gray-400">
                                  {t('performance:no_data_found')}
                                </div>
                              </div>
                              <PercentagePill value="0%" tone="red" />
                            </div>
                          </div>
                        );
                      }

                      const tone = getToneFromColor(cell.color);
                      return (
                        <div
                          key={`cell-potential-${cell.potential}-performance-${cell.performance}`}
                          className="tw-border tw-border-gray-200 tw-rounded-md tw-bg-white
                          tw-p-4 tw-flex tw-flex-col tw-justify-between tw-min-h-[140px]
                          tw-cursor-pointer hover:tw-shadow-md tw-transition-shadow"
                          onClick={() => handleGridCellClick(
                              cell.potential,
                              cell.performance
                            )}
                        >
                          <div className="tw-flex tw-justify-between tw-items-start">
                            <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
                              <div
                                className="tw-w-[10px] tw-h-4 tw-rounded-[3px]"
                                style={{ backgroundColor: cell.color }}
                              />
                              <div className="tw-flex tw-flex-row tw-items-center tw-gap-1">
                                <div className="tw-text-base tw-font-semibold tw-text-gray-textDark">
                                  {cell.title}
                                </div>
                                <span className="tw-text-sm tw-font-medium tw-text-gray-500">
                                  ({cell.percentage}%)
                                </span>
                              </div>
                            </div>
                            <Icon
                              type="chevron_right"
                              className="tw-text-gray-500"
                            />
                          </div>
                          <div className="tw-mt-3 tw-flex tw-items-center tw-justify-between">
                            {renderAvatarGroup(
                              // [...cell.users, ...employees],
                              cell.users,
                              cell.title
                            )}
                            <PercentagePill
                              value={`${cell.retentionRate}%`}
                              tone={tone}
                              retentionRisk={cell.retentionRisk}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottom performance scale + caption */}
          <div className="tw-flex tw-flex-col tw-gap-4">
            <div
              className="tw-grid tw-grid-cols-4 tw-gap-4 tw-mt-4"
              style={{ gridTemplateColumns: '40px 1fr 1fr 1fr' }}
            >
              <div />
              {performanceLabels?.map((label: string, idx: number) => {
                const performanceValue = idx + 1;
                return (
                  <div
                    key={`performance-label-${performanceValue}-${label}`}
                    className={`tw-rounded-md tw-text-gray-textDark tw-text-base 
                      tw-font-medium tw-text-center tw-py-2 ${getPerformanceLabelColor(
                        performanceValue
                      )}`}
                  >
                    {label}{' '}
                    <span className="tw-text-sm tw-text-gray-500">
                      ({performanceDistribution[idx] || 0}%)
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="tw-h-[2px] tw-bg-gray-border tw-ml-[54px]" />
            <div
              className="tw-flex tw-items-center tw-justify-center tw-gap-1
            tw-text-gray-500 tw-text-base tw-font-medium tw-ml-[60px]"
            >
              <span>{t('performance:performance_label')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const [networkIds, functionIds, gridKeys] = useMemo(() => {
    return [
      predicates.find(
        (p) => p.filter === EPredicateFilters[EPredicateFields.NETWORK]
      )?.value.value,
      predicates.find(
        (p) => p.filter === EPredicateFilters[EPredicateFields.FUNCTION]
      )?.value.value,
      predicates.find(
        (p) => p.filter === EPredicateFilters[EPredicateFields.GRID_KEY]
      )?.value.value,
    ];
  }, [predicates]);

  const { sortBy } = useMemo(() => {
    if (!sortConfig) return { sortBy: undefined };

    const { key, sortOrder } = sortConfig;

    if (key === 'employee') {
      return {
        sortBy: `name_${sortOrder}`,
      };
    }

    return {
      sortBy: `${key}_${sortOrder}`,
    };
  }, [sortConfig]);

  const payload = useMemo(
    () => ({
      show_all_evaluations: !!allTalentReviews,
      ...(networkIds ? { networkIds } : {}),
      ...(functionIds ? { functionIds } : {}),
      ...(gridKeys ? { gridKeys } : {}),
      ...(sortBy ? { sortBy } : {}),
    }),
    [networkIds, functionIds, gridKeys, sortBy, allTalentReviews]
  );

  // Grid payload without gridKeys filter
  const gridPayload = useMemo(
    () => ({
      show_all_evaluations: !!allTalentReviews,
      ...(networkIds ? { networkIds } : {}),
      ...(functionIds ? { functionIds } : {}),
    }),
    [networkIds, functionIds, allTalentReviews]
  );

  const apiConfig = {
    useCursor: true,
    limit: 20,
    payload,
    transform: (res: any) => {
      return {
        ...res,
      };
    },
  };

  const {
    data: talentReviewsDataRaw,
    loading: talentReviewsLoading,
    refetch: talentReviewsRefetch,
    reset: talentReviewsReset,
    meta: talentReviewsMetaData,
  } = useRequest(
    `/v1/organisations/${organisation.id}/performance/reports/talent-reviews/list`,
    apiConfig
  );

  const gridApiConfig = {
    payload: gridPayload,
    transform: (res: any) => {
      return {
        ...res,
      };
    },
  };

  const {
    data: gridDataRaw,
    loading: gridLoading,
    reset: gridReset,
  } = useRequest(
    `/v1/organisations/${organisation?.id}/performance/reports/talent-reviews/grid`,
    gridApiConfig
  );

  const nineBoxCells = useMemo(() => {
    const rows: Array<
      Array<{
        title: string;
        users: Array<{ id: string; full_name: string; profile_img?: string }>;
        percentage: number;
        retentionRate: number;
        retentionRisk: Array<{ score: number; total_users: number }>;
        color: string;
        potential: number;
        performance: number;
      } | null>
    > = [[], [], []];

    for (let i = 0; i < 3; i++) {
      rows[i] = [null, null, null];
    }

    const gridData = gridDataRaw?.grid || gridDataRaw?.data?.grid;
    // const statistics = gridDataRaw?.statistics || gridDataRaw?.data?.statistics;

    if (gridData && Array.isArray(gridData) && gridData.length > 0) {
      // const totalEmployees = statistics?.total_employees || 1;

      gridData.forEach((cell: any) => {
        if (
          !cell ||
          typeof cell.potential !== 'number' ||
          typeof cell.performance !== 'number'
        ) {
          return;
        }

        const rowIndex = 3 - cell.potential;
        const colIndex = cell.performance - 1;
        if (rowIndex >= 0 && rowIndex < 3 && colIndex >= 0 && colIndex < 3) {
          const retentionRate = calculateRetentionRate(
            cell.retention_risk || []
          );

          rows[rowIndex][colIndex] = {
            title: cell.label || '',
            users: cell.user_preview || [],
            percentage: cell.percentage || 0,
            retentionRate,
            retentionRisk: cell.retention_risk || [],
            color: cell.color || '#DC2626',
            potential: cell.potential,
            performance: cell.performance,
          };
        }
      });
    }

    return rows;
  }, [gridDataRaw]);

  const potentialLabels = gridDataRaw?.potential_labels;
  const performanceLabels = gridDataRaw?.performance_labels;
  const statistics = gridDataRaw?.statistics || gridDataRaw?.data?.statistics;

  const potentialDistribution = useMemo(() => {
    if (!statistics?.potential_distribution) return [];
    const dist = statistics.potential_distribution;
    // Returns [high, moderate, low] to match reversed potential labels order
    return [
      dist.high?.percentage || 0,
      dist.moderate?.percentage || 0,
      dist.low?.percentage || 0,
    ];
  }, [statistics]);

  const performanceDistribution = useMemo(() => {
    if (!statistics?.performance_distribution) return [];
    const dist = statistics.performance_distribution;
    // Returns [low, moderate, high] to match performance labels order
    return [
      dist.low?.percentage || 0,
      dist.moderate?.percentage || 0,
      dist.high?.percentage || 0,
    ];
  }, [statistics]);

  const reversedPotentialLabels = useMemo(() => {
    return potentialLabels ? [...potentialLabels].reverse() : [];
  }, [potentialLabels]);

  const gridData = talentReviewsMetaData?.related?.grid || [];

  const columns = useColumns(gridData);

  useEffect(() => {
    if (!didMount) return;
    talentReviewsReset();
  }, [payload, didMount, talentReviewsReset]);

  useEffect(() => {
    if (!didMount) return;
    gridReset();
  }, [gridPayload, didMount, gridReset]);

  const handleTalentReviewsSort = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) {
      setSortConfig({ key, sortOrder: 'asc' });
      return;
    }
    setSortConfig({
      key,
      sortOrder: sortConfig.sortOrder === 'asc' ? 'desc' : 'asc',
    });
  };

  return (
    <Container>
      <Container.Content>
        <div className="!tw-flex !tw-flex-row !tw-items-center !tw-justify-between">
          <FiltersToolbar
            searchEnabled={false}
            predicates={predicates}
            setPredicates={setPredicates}
            searchPlaceholder={t('common:search_list_search_placeholder')}
          />
          {/* <Button
            icon="download"
            iconColor="grey"
            size="large"
            type="white"
            className="!tw-mr-2 !tw-mt-[-4px] !tw-text-gray-text"
          >
            {t('performance:export')}
          </Button> */}
          <div
            className="tw-bg-gray-100 tw-min-w-[235px] tw-flex-row tw-justify-around
            tw-items-center tw-rounded-lg !tw-px-1 !tw-py-1 tw-flex
            tw-border tw-border-solid tw-border-neutral-200 !tw-mt-[-4px] tw-me-2"
          >
            <span
              className={`!tw-px-2 !tw-py-1 tw-cursor-pointer ${
                !allTalentReviews
                  ? '!tw-bg-white tw-text-gray-textDark tw-shadow-sm !tw-rounded-md'
                  : 'tw-text-gray-text hover:tw-text-gray-900'
              }`}
              onClick={() => setAllTalentReviews(false)}
            >
              {t('performance:last_evaluation')}
            </span>
            <span
              className={`!tw-px-2 !tw-py-1 tw-cursor-pointer ${
                allTalentReviews
                  ? '!tw-bg-white tw-text-gray-textDark tw-shadow-sm !tw-rounded-md'
                  : 'tw-text-gray-text hover:tw-text-gray-900'
              }`}
              onClick={() => setAllTalentReviews(true)}
            >
              {t('performance:all_evaluations')}
            </span>
          </div>
          <div
            className="tw-bg-gray-100 tw-min-w-[70px] tw-flex-row tw-justify-around
            tw-items-center tw-rounded-lg !tw-px-1 !tw-py-1 tw-flex
            tw-border tw-border-solid tw-border-neutral-200 !tw-mt-[-4px]"
          >
            <Icon
              type="grid_table_view"
              size={18}
              tabIndex={0}
              color="grey"
              aria-label={t('performance:table_view') || 'table_view'}
              onClick={() => setActiveTab('table')}
              onKeyDown={(e: any) => {
                if (e.key === 'Enter' || e.key === ' ') setActiveTab('table');
              }}
              className={`tw-w-5 tw-h-5 tw-rounded-md tw-flex tw-items-center 
                tw-justify-center tw-transition tw-outline-none !tw-px-2 !tw-py-1 !tw-cursor-pointer ${
                  activeTab === 'table'
                    ? '!tw-bg-white !tw-text-gray-textDark !tw-shadow-sm !tw-rounded-md'
                    : 'tw-text-gray-text hover:tw-text-gray-900'
                }`}
            />
            <Icon
              type="table_view"
              size={18}
              tabIndex={0}
              aria-label={t('performance:nine_box_grid') || 'nine_box_grid'}
              onClick={() => setActiveTab('grid')}
              onKeyDown={(e: any) => {
                if (e.key === 'Enter' || e.key === ' ') setActiveTab('grid');
              }}
              color="grey"
              className={`tw-w-5 tw-h-5 tw-rounded-md tw-flex tw-items-center 
                tw-justify-center tw-transition tw-outline-none !tw-px-2 !tw-py-1 tw-cursor-pointer ${
                  activeTab === 'grid'
                    ? '!tw-bg-white tw-text-gray-textDark tw-shadow-sm !tw-rounded-md'
                    : 'tw-text-gray-text hover:tw-text-gray-900'
                }`}
            />
          </div>
        </div>

        {activeTab === 'table' ? (
          <NineBoxGrid />
        ) : (
          <Overview>
            <DataTable
              columns={columns}
              data={talentReviewsDataRaw || []}
              loading={talentReviewsLoading}
              onShowMore={talentReviewsRefetch}
              sortConfig={sortConfig}
              onSort={handleTalentReviewsSort}
              pagination={{
                ...omit(apiConfig, 'payload'),
                ...talentReviewsMetaData?.pagination,
                mode: 'loadmore',
              }}
              emptyView={
                <Placeholder
                  title={t('performance:no_employees_placeholder')}
                />
              }
            />
          </Overview>
        )}
      </Container.Content>

      {/* <Drawer
        show={showDrawer}
        onHide={() => setShowDrawer(false)}
        title={
          selectedUser && (
            <Profile
              user={{
                full_name: selectedUser.name,
                profile_img: `https://i.pravatar.cc/80?img=${selectedUser.id}`,
              }}
              size={28}
              className="tw-font-medium tw-text-gray-textDarkest tw-text-base"
            />
          )
        }
        size="medium"
      >
        <div className="tw-p-6 tw-flex tw-flex-col tw-gap-6">
          <div className="tw-flex tw-flex-col tw-gap-4">
            <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
              <div className="tw-font-medium tw-w-1/2 tw-text-gray-muted tw-text-base">
                Date:
              </div>
              <div className="tw-font-medium tw-text-gray-textDarkest tw-text-base">
                March 13,2025
              </div>
            </div>
            <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
              <div className="tw-font-medium tw-w-1/2 tw-text-gray-muted tw-text-base">
                Manager:
              </div>
              <Profile
                user={{
                  full_name: selectedUser ? selectedUser.name : 'John Doe',
                  profile_img: `https://i.pravatar.cc/80?img=${
                    selectedUser ? selectedUser.id : 1
                  }`,
                }}
                size={20}
              />
            </div>
          </div>

          <div className="tw-h-[1px] tw-bg-gray-neutral-50 tw-flex-grow" />

          <div className="tw-flex tw-flex-col tw-gap-4">
            <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
              <div className="tw-font-semibold tw-text-gray-muted tw-text-xs tw-uppercase">
                Talent Review
              </div>
            </div>
            <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
              <div className="tw-font-medium tw-w-1/2 tw-text-gray-muted tw-text-base tw-uppercase">
                Employee potential:
              </div>
              <Tag level={2} />
            </div>
            <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
              <div className="tw-font-medium tw-w-1/2 tw-text-gray-muted tw-text-base tw-uppercase">
                Employee performance:
              </div>
              <Tag level={1} />
            </div>
            <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
              <div className="tw-font-medium tw-w-1/2 tw-text-gray-muted tw-text-base tw-uppercase">
                Retention risk:
              </div>
              <Tag level={3} />
            </div>
          </div>

          <div className="tw-h-[1px] tw-bg-gray-neutral-50 tw-flex-grow" />

          <div className="tw-flex tw-flex-col tw-gap-4">
            <div className="tw-font-muted tw-text-gray-muted tw-text-xs tw-uppercase">
              Impact of loss
            </div>
            <p className="tw-text-gray-textDarkest tw-text-base tw-font-medium">
              Sophia has shown noticeable improvement in time management,
              especially during peak hours. Needs to continue refining
              prioritization when under pressure. Demonstrates strong ownership
              of assigned tasks but occasionally hesitates to escalate issues
              early. Encourage more proactive communication.
            </p>
          </div>
        </div>
      </Drawer> */}

      <ModalWindow
        show={showUsersModal}
        onHide={() => setShowUsersModal(false)}
        size="small"
      >
        <ModalContent
          title={modalCellData?.title}
          onHide={() => setShowUsersModal(false)}
          hideFooter
        >
          <div className="tw-flex tw-flex-col tw-gap-3 tw-py-2">
            {modalCellData?.users &&
              modalCellData.users.map((user) => (
                <a
                  href={`/admin/users/${user.id}/about`}
                  target="_blank"
                  key={user.id}
                  rel="noreferrer"
                >
                  <Profile user={user} size={32} />
                </a>
              ))}
          </div>
        </ModalContent>
      </ModalWindow>
    </Container>
  );
};

export default TalentReviewsContainer;
